import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toChartRows } from './compareSeries';

// One Segment reads as one colour everywhere: its bars, its cumulative line,
// and its row in the summary box. Colours are assigned by selection order and
// are the only thing distinguishing Segments, so they are kept far apart in
// hue rather than being shades of one another.
export const SEGMENT_COLORS = ['#1967D2', '#0F9D58', '#F29900', '#D93025', '#8430CE', '#00838F'];
// A second channel for the same identity, so Segments stay separable in
// greyscale, in print, and for a viewer who cannot distinguish the hues.
const SEGMENT_PATTERNS = [null, 'diagonal', 'dots', 'horizontal', 'grid', 'vertical'];

export const segmentColor = (index) => SEGMENT_COLORS[index % SEGMENT_COLORS.length];
const patternID = (index) => `compare-pattern-${index}`;

// SVG fill patterns, one per Segment beyond the first. The first Segment stays
// solid so the common two-Segment case is solid-versus-textured.
function PatternDefs({ count }) {
  return (
    <defs>
      {Array.from({ length: count }, (_, index) => {
        const shape = SEGMENT_PATTERNS[index % SEGMENT_PATTERNS.length];
        if (!shape) return null;
        const color = segmentColor(index);
        return (
          <pattern key={index} id={patternID(index)} patternUnits="userSpaceOnUse" width="6" height="6">
            <rect width="6" height="6" fill={color} />
            {shape === 'diagonal' && <path d="M0,6 l6,-6 M-1,1 l2,-2 M5,7 l2,-2" stroke="#FFFFFF" strokeWidth="1.4" />}
            {shape === 'dots' && <circle cx="3" cy="3" r="1.4" fill="#FFFFFF" />}
            {shape === 'horizontal' && <path d="M0,2 h6 M0,5 h6" stroke="#FFFFFF" strokeWidth="1.2" />}
            {shape === 'grid' && <path d="M0,3 h6 M3,0 v6" stroke="#FFFFFF" strokeWidth="1.1" />}
            {shape === 'vertical' && <path d="M2,0 v6 M5,0 v6" stroke="#FFFFFF" strokeWidth="1.2" />}
          </pattern>
        );
      })}
    </defs>
  );
}

export const segmentFill = (index) =>
  (SEGMENT_PATTERNS[index % SEGMENT_PATTERNS.length] ? `url(#${patternID(index)})` : segmentColor(index));

/**
 * The Describe distribution, split by Segment: categories along the axis, one
 * bar per Segment in each category, and optionally each Segment's own
 * cumulative percentage line.
 */
export default function CompareChart({
  series,
  showProportion = false,
  showCumulative = true,
  height = 380,
  chartRef,
}) {
  if (!series || series.kind === 'none' || !series.categories.length) {
    return (
      <div role="status" style={{ padding: 32, textAlign: 'center', color: '#5F6368', fontSize: 13 }}>
        {series?.reason || 'No comparable distribution was returned for these Segments.'}
      </div>
    );
  }

  const rows = toChartRows(series, { showProportion });
  // Room for angled category labels. This is given to the axis ONLY — also
  // adding it to the chart margin reserves it twice and collapses the plot
  // area to a sliver, which is what made the chart unreadable.
  const longest = Math.max(...series.categories.map(c => String(c).length));
  const axisHeight = Math.min(150, 44 + longest * 4.5);
  // Wide bars over few categories look like blocks rather than a distribution.
  const maxBarSize = Math.max(18, Math.min(64, 420 / Math.max(1, series.categories.length * series.segments.length)));

  return (
    <div ref={chartRef} style={{ width: '100%' }}>
      <ResponsiveContainer width="100%" height={height + axisHeight}>
        <ComposedChart data={rows} margin={{ top: 16, right: showCumulative ? 56 : 24, left: 8, bottom: 8 }}>
          <PatternDefs count={series.segments.length} />
          <CartesianGrid stroke="#F1F3F4" vertical={false} />
          <XAxis
            dataKey="category"
            interval={0}
            angle={-30}
            textAnchor="end"
            height={axisHeight}
            tick={{ fill: '#5F6368', fontSize: 12 }}
            axisLine={{ stroke: '#E8EAED' }}
            tickLine={{ stroke: '#E8EAED' }}
          />
          <YAxis
            yAxisId="value"
            allowDecimals={showProportion}
            tick={{ fill: '#5F6368', fontSize: 12 }}
            axisLine={{ stroke: '#E8EAED' }}
            tickLine={{ stroke: '#E8EAED' }}
            tickFormatter={value => (showProportion ? `${value}%` : value)}
          />
          {showCumulative && (
            <YAxis
              yAxisId="cumulative"
              orientation="right"
              domain={[0, 100]}
              tick={{ fill: '#5F6368', fontSize: 12 }}
              axisLine={{ stroke: '#E8EAED' }}
              tickLine={{ stroke: '#E8EAED' }}
              tickFormatter={value => `${value}%`}
            />
          )}
          <Tooltip
            contentStyle={{ fontFamily: 'var(--font-family-primary)', fontSize: 12 }}
            formatter={(value, name) => [
              typeof value === 'number' ? (showProportion || String(name).includes('cumulative') ? `${value.toFixed(1)}%` : value) : value,
              name,
            ]}
          />
          <Legend
            verticalAlign="top"
            align="left"
            wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
          />
          {series.segments.map((segment, index) => (
            <Bar
              key={segment.key}
              yAxisId="value"
              dataKey={`value__${segment.key}`}
              name={`${segment.name} (${series.countLetter || 'c'}=${segment.total})`}
              fill={segmentFill(index)}
              stroke={segmentColor(index)}
              strokeWidth={1}
              maxBarSize={maxBarSize}
              isAnimationActive={false}
            />
          ))}
          {showCumulative && series.segments.map((segment, index) => (
            <Line
              key={`cum-${segment.key}`}
              yAxisId="cumulative"
              type="monotone"
              dataKey={`cum__${segment.key}`}
              name={`${segment.name} cumulative`}
              stroke={segmentColor(index)}
              strokeWidth={2}
              strokeDasharray={index === 0 ? undefined : '5 3'}
              dot={{ r: 2.5, fill: segmentColor(index) }}
              isAnimationActive={false}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      <div style={{ fontSize: 11, color: '#9AA0A6', marginTop: 4 }}>
        Bars show each Segment's {showProportion ? 'share of its own answers' : 'answer counts'}.
        {showCumulative && ' Lines show each Segment\'s running percentage across the categories, left to right.'}
      </div>
    </div>
  );
}
